import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { useTranslation } from "react-i18next";
import CalendarChek from "@/assets/icon/svgs/CalendarChek.svg?react";
import HouseManagerApi from "@/api-client/api/services/houseManager/houseManager";
import BillsService, { IHouseManagerBillsPage } from "@/api-client/api/services/billsService/billsService";
import { Invoice } from "@/api-client/api/services/billingService/billingService";

export function ConfirmClosePeriod({
  trigger,
  setPeriodMonthYear,
  selectedBuildAddress,
  setSelectedPeriodOptions,
}: {
  trigger: React.ReactNode;
  setPeriodMonthYear: (periodMonthYear: string | undefined) => void;
  selectedBuildAddress: { label: string; value: string };
  setSelectedPeriodOptions: (selectedPeriodOptions: {
    name: string;
    value: string;
  }) => void;
}) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [sendTrue, setSendTrue] = useState(false);

  const handleDialogClose = (isOpen: boolean) => {
    setIsOpen(isOpen);
  };

  const fetchBills = async () => {
    try {
      const pageSize = 40;
      let allInvoices: Invoice[] = [];
      let currentPage = 1;
      let totalPages = 1;

      do {
        const bills = await BillsService.getBills<IHouseManagerBillsPage>(
          undefined,
          undefined,
          undefined,
          undefined,
          currentPage,
          pageSize,
          undefined,
          selectedBuildAddress.value,
          undefined,
          true,
          "all"
        );

        allInvoices = [...allInvoices, ...(bills.apartments?.flatMap(apartment => apartment.invoices) ?? [])];

        totalPages = bills.total_pages;
        currentPage++;
      } while (currentPage <= totalPages);

      const allInvoicesFilled = allInvoices.every((invoice) => {
        return (
          invoice.invoice_id &&
          invoice.apartment_address &&
          invoice.apartment_number &&
          invoice.due_date &&
          invoice.invoice_name &&
          invoice.total_amount
        );
      });

      setSendTrue(allInvoicesFilled);
    } catch (error) {
      console.error("Error fetching bills:", error);
      setSendTrue(false);
    }
  };

  const clousePeriod = async () => {
    try {
      await HouseManagerApi.postClosePeriod(selectedBuildAddress.value);
      setIsOpen(false);
      setPeriodMonthYear(undefined);
      setSelectedPeriodOptions({
        name: t("house_manager.closed_periods"),
        value: "closed_periods",
      });
    } catch (error) {
      console.error("Error closing period:", error);
    }
  };

  useEffect(() => {
    if (!isOpen) return;
    fetchBills();
  }, [isOpen]);

  return (
    <Dialog open={isOpen} onOpenChange={handleDialogClose} modal={false}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>

      {isOpen && (
        <div className="fixed inset-0 bg-black/10 backdrop-blur-[4px] z-40 pointer-events-none"></div>
      )}
      <DialogContent className="max-h-full overflow-auto w-[440px] pb-[40px]">
        <DialogHeader className="mb-[16px]">
          <DialogTitle className="text-[24px] font-medium flex items-center justify-center gap-4">
            {sendTrue ? (
              <CalendarChek width={48} height={48} />
            ) : (
              <div className="border-[3px] border-[#FF0000] text-[#FF0000] rounded-full w-[48px] h-[48px] flex items-center justify-center">
                !
              </div>
            )}
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-4 mb-4">
          <span className="text-[24px] font-medium text-center">
            {sendTrue
              ? t("popUpConfirmClosePeriod.confirm_close_period")
              : t("popUpConfirmClosePeriod.confirm_close_period_error")}
          </span>

          <span className="text-[18px] font-medium text-[#00000080] text-center">
            {sendTrue
              ? t("popUpConfirmClosePeriod.confirm_close_period_description")
              : t(
                  "popUpConfirmClosePeriod.confirm_close_period_error_description"
                )}
          </span>
        </div>

        <Button
          type="submit"
          variant="system"
          size="system"
          disabled={false}
          loading={false}
          onClick={() => {
            if (sendTrue) {
              clousePeriod();
            } else {
              setIsOpen(false);
            }
          }}
        >
          {sendTrue
            ? t("popUpConfirmClosePeriod.confirm_and_send_bills")
            : t("popUpConfirmClosePeriod.confirm_and_send_bills_error")}
        </Button>
      </DialogContent>
    </Dialog>
  );
}
